// =====================================================================
// MPS-279 — RFQ auto-save
// Author: Akarsh Singh | 2026-08-27
//
// Saves RFQ progress in the background so a buyer does not lose work to a
// dropped connection, a closed tab or a session timeout.
//
// This deliberately does NOT reuse the components' own save() methods. Those
// are written for a button press: they open the full-screen loader, list
// missing fields, and advance to the next tab on success. All three are wrong
// for a save that happens while somebody is typing, so this service calls the
// draft API directly and does none of them.
// =====================================================================
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { AbstractControl } from '@angular/forms';

/** What the status line is showing. Nothing is displayed for 'idle'. */
export type AutoSaveState = 'idle' | 'saving' | 'saved' | 'failed';

export interface AutoSaveStatus {
  state: AutoSaveState;
  /** When the last successful save completed; drives "Last saved N minutes ago". */
  lastSavedAt: Date | null;
}

/** What a screen must provide for its own step to be auto-saved. */
export interface AutoSaveHandler {
  /** Builds the payload exactly as the manual draft save would. */
  buildPayload: () => any;
  /** Sends it. Resolves on success, rejects on failure. */
  persist: (payload: any) => Promise<any>;
  /**
   * Whether there is enough information to save yet. The header uses this to
   * hold back until the mandatory fields exist, so a buyer who types one
   * character and leaves does not create an empty RFQ.
   */
  canSave: () => boolean;
}

@Injectable({ providedIn: 'root' })
export class RfqAutoSaveService implements OnDestroy {

  /** Wait this long after the last keystroke before saving. */
  private static readonly QUIET_PERIOD_MS = 3000;

  /** Save at least this often while a step has unsaved edits. */
  private static readonly HEARTBEAT_MS = 90000;

  /** Only admit to saving once it has taken longer than this. */
  private static readonly SLOW_SAVE_MS = 2000;

  /** How long the "Saved" acknowledgement stays before settling. */
  private static readonly SAVED_FLASH_MS = 800;

  private readonly statusSubject = new BehaviorSubject<AutoSaveStatus>({
    state: 'idle',
    lastSavedAt: null,
  });

  readonly status$ = this.statusSubject.asObservable();

  private handler: AutoSaveHandler | null = null;
  private storageKey = '';

  private subscriptions = new Subscription();

  private slowSaveTimer: any = null;
  private savedFlashTimer: any = null;

  /** Serialises saves so a slow response cannot overwrite a newer one. */
  private inFlight: Promise<void> = Promise.resolve();

  /** Snapshot of the last payload written, so unchanged forms are skipped. */
  private lastSavedSignature = '';

  /** Set while edits have happened that no successful save covers yet. */
  private hasPendingChanges = false;

  private onlineListener = () => this.retryNow();

  constructor(private zone: NgZone) {}

  /**
   * Starts watching a step. Call once the screen has finished loading its own
   * data, otherwise the initial patching looks like a user edit.
   */
  start(control: AbstractControl, handler: AutoSaveHandler, storageKey: string): void {
    this.stop();

    this.handler = handler;
    this.storageKey = storageKey;

    // Everything below is bookkeeping and must not provoke change detection.
    // Kept outside Angular so that typing stays smooth: a debounce timer and a
    // heartbeat inside the zone would schedule a change-detection pass on every
    // keystroke and every tick, on a form with hundreds of controls.
    this.zone.runOutsideAngular(() => {

      // Marked immediately, before the debounce: the heartbeat needs to know
      // there is unsaved work even while the buyer is still typing.
      this.subscriptions.add(
        control.valueChanges.subscribe(() => { this.hasPendingChanges = true; })
      );

      this.subscriptions.add(
        control.valueChanges
          .pipe(debounceTime(RfqAutoSaveService.QUIET_PERIOD_MS))
          .subscribe(() => this.requestSave())
      );

      // A heartbeat for the buyer who keeps typing without ever pausing long
      // enough for the quiet period to elapse.
      this.subscriptions.add(
        timer(RfqAutoSaveService.HEARTBEAT_MS, RfqAutoSaveService.HEARTBEAT_MS)
          .pipe(filter(() => this.hasPendingChanges))
          .subscribe(() => this.requestSave())
      );
    });

    window.addEventListener('online', this.onlineListener);
  }

  /** Stops watching. Safe to call when nothing is running. */
  stop(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    window.removeEventListener('online', this.onlineListener);
    this.clearTimers();
    this.handler = null;
    this.hasPendingChanges = false;
    this.lastSavedSignature = '';
    this.setStatus({ state: 'idle', lastSavedAt: null });
  }

  /** Retry after a failure, from the status line or from coming back online. */
  retryNow(): void {
    if (this.statusSubject.value.state === 'failed') {
      this.requestSave();
    }
  }

  /**
   * The watchers run outside Angular to keep typing smooth, so every status
   * change has to step back in or the status line would never repaint.
   */
  private setStatus(status: AutoSaveStatus): void {
    this.zone.run(() => this.statusSubject.next(status));
  }

  private requestSave(): void {
    const handler = this.handler;
    if (!handler || !handler.canSave()) { return; }

    let payload: any;
    try {
      payload = handler.buildPayload();
    } catch {
      // A payload that cannot even be built is not worth reporting to the
      // buyer; the step is mid-edit and the next attempt will succeed.
      return;
    }

    const signature = this.signatureOf(payload);
    if (signature === this.lastSavedSignature) {
      this.hasPendingChanges = false;
      return;
    }

    // Queue behind any save already running, so writes land in order.
    this.inFlight = this.inFlight.then(() => this.performSave(handler, payload, signature));
  }

  private async performSave(handler: AutoSaveHandler, payload: any, signature: string): Promise<void> {
    // Stay silent unless the save turns out to be slow. Most finish in a few
    // hundred milliseconds and a spinner would only flicker.
    this.slowSaveTimer = setTimeout(() => {
      this.setStatus({ ...this.statusSubject.value, state: 'saving' });
    }, RfqAutoSaveService.SLOW_SAVE_MS);

    try {
      await handler.persist(payload);

      this.clearSlowTimer();
      this.lastSavedSignature = signature;
      this.hasPendingChanges = false;
      this.clearStoredPayload();

      const savedAt = new Date();
      this.setStatus({ state: 'saved', lastSavedAt: savedAt });

      // Let the acknowledgement settle rather than sit there.
      clearTimeout(this.savedFlashTimer);
      this.savedFlashTimer = setTimeout(() => {
        if (this.statusSubject.value.state === 'saved') {
          this.setStatus({ state: 'idle', lastSavedAt: savedAt });
        }
      }, RfqAutoSaveService.SAVED_FLASH_MS);

    } catch {
      this.clearSlowTimer();
      // Hold the work locally so a refresh does not lose it, and keep the
      // failure on screen until it is actually resolved.
      this.storePayloadLocally(payload);
      this.setStatus({
        state: 'failed',
        lastSavedAt: this.statusSubject.value.lastSavedAt,
      });
    }
  }

  /**
   * Cheap comparison of one payload against the last one saved, so an
   * unchanged form is never sent. Falls back to always saving if the payload
   * cannot be serialised, which is safer than skipping a real change.
   */
  private signatureOf(payload: any): string {
    try {
      return JSON.stringify(payload);
    } catch {
      return String(Date.now());
    }
  }

  private storePayloadLocally(payload: any): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        savedAt: new Date().toISOString(),
        payload,
      }));
    } catch {
      // Storage can be full or blocked; the in-memory retry still stands.
    }
  }

  private clearStoredPayload(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Nothing useful to do if storage is unavailable.
    }
  }

  /** Any work held back by a failed save, for offering recovery on return. */
  readStoredPayload(storageKey: string): { savedAt: string; payload: any } | null {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private clearSlowTimer(): void {
    clearTimeout(this.slowSaveTimer);
    this.slowSaveTimer = null;
  }

  private clearTimers(): void {
    this.clearSlowTimer();
    clearTimeout(this.savedFlashTimer);
    this.savedFlashTimer = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
